export interface VideoComment {
  id: string;
  userId: string;
  userName: string;
  time: number; // seconds into the video where the comment was added
  text: string;
  createdAt: number; // epoch milliseconds
}

// key = video url
export type VideoAnnotations = Record<string, VideoComment[]>;

const STORAGE_KEY = 'geneo_video_annotations_v1';

export const createCommentId = (): string =>
  `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

const readAll = (): VideoAnnotations => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as VideoAnnotations) : {};
  } catch (error) {
    console.error('Failed to read video annotations from localStorage', error);
    return {};
  }
};

const writeAll = (data: VideoAnnotations): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Failed to persist video annotations to localStorage', error);
  }
};

const sortByCreatedAt = (comments: VideoComment[]): VideoComment[] =>
  [...comments].sort((a, b) => a.createdAt - b.createdAt);

export const getVideoComments = (url: string): VideoComment[] => {
  const all = readAll();
  return sortByCreatedAt(all[url] || []);
};

export const saveVideoComments = (
  url: string,
  comments: VideoComment[]
): void => {
  const all = readAll();
  if (comments.length > 0) {
    all[url] = sortByCreatedAt(comments);
  } else {
    delete all[url];
  }
  writeAll(all);
};

export const addVideoComment = (
  url: string,
  comment: VideoComment
): VideoComment[] => {
  const next = sortByCreatedAt([...getVideoComments(url), comment]);
  saveVideoComments(url, next);
  return next;
};

export const deleteVideoComment = (
  url: string,
  commentId: string
): VideoComment[] => {
  const next = getVideoComments(url).filter((c) => c.id !== commentId);
  saveVideoComments(url, next);
  return next;
};

export interface LoggedInUser {
  id: string;
  name: string;
}

const joinName = (first?: string, last?: string): string =>
  [first, last].filter(Boolean).join(' ').trim();

// reads user id + name from persist:root so shared-ui does not depend on app redux
export const resolveLoggedInUser = (
  override?: Partial<LoggedInUser>
): LoggedInUser => {
  let id = override?.id || '';
  let name = override?.name || '';
  try {
    if (!id) {
      id = localStorage.getItem('userId') || '';
    }
    if (!name || !id) {
      const raw = localStorage.getItem('persist:root');
      if (raw) {
        const root = JSON.parse(raw);
        const authRaw = root.auth;
        const auth =
          typeof authRaw === 'string' ? JSON.parse(authRaw) : authRaw || {};
        const info = auth.user_info || auth.userInfo || {};
        const admin = info.adminInfo || {};
        if (!name) {
          name =
            joinName(info.firstName, info.lastName) ||
            joinName(admin.firstName, admin.lastName);
        }
        if (!id) {
          id = String(
            info.teacherProfileId ||
              info.studentProfileId ||
              admin.adminId ||
              ''
          );
        }
      }
    }
  } catch (error) {
    console.error('Failed to resolve logged-in user for video comments', error);
  }
  return { id, name };
};

// formats seconds as mm:ss (or h:mm:ss for long videos)
export const formatVideoTime = (totalSeconds: number): string => {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  if (hrs > 0) {
    return `${hrs}:${pad(mins)}:${pad(secs)}`;
  }
  return `${pad(mins)}:${pad(secs)}`;
};
